import React, { useCallback, useMemo, useContext } from 'react'
import OptionsMenu from '../../Common/OptionsMenu/OptionsMenu'
import SectionFormContainer from '../components/SectionFormContainer'
import BoardingWizardContext from './BoardingWizardContext'
import useBoardingVisit from '../../../hooks/useBoardingVisit'
import useDaycareVisit from '../../../hooks/useDaycareVisit'
import useSelectedHousehold from '../../../hooks/useSelectedHousehold'
import useDayRestrictions from '../../../hooks/useDayRestrictions'
import { formatNumsToDollars, isVipMembershipType } from '../../../utils'
import { BOARDING_FIELDS } from './config'
import moment from 'moment'
import StyledAlert from '../../Common/StyledAlert'

const Dropoff = (props) => {
	const {
		wizardContextProvider: { wizard_state, setWizardAttributeAndValues }
	} = useContext(BoardingWizardContext)
	const { title = '', subtext = '' } = props
	const { loading, standardBoardingDropoffHours } = useBoardingVisit()
	const { daycare } = useDaycareVisit()
	const { membershipsAvailableForDate } = useSelectedHousehold()
	const { getDateRestrictions } = useDayRestrictions()

	const firstDay = useMemo(() => {
		const { dates } = wizard_state
		const [day] = dates
		return day || null
	}, [wizard_state])

	const daycareNotAvailableOnDate = useMemo(() => {
		if (!firstDay) {
			return false
		}

		const hasRestriction = getDateRestrictions({ date: firstDay })
		if (!hasRestriction) {
			return false
		}

		const { daycareAllowed } = hasRestriction
		return !daycareAllowed
	}, [firstDay, getDateRestrictions])

	const isVipOrPetCareOnDate = useMemo(() => {
		const output = { vip: false, petcare: false }
		if (!firstDay) {
			return output
		}

		const validMembershipsForDate = membershipsAvailableForDate(firstDay)
		const memTypes = Object.keys(validMembershipsForDate)

		if (memTypes.length === 0) {
			return output
		}

		memTypes.forEach((type) => {
			if (isVipMembershipType(type)) {
				output.vip = true
			}
			if (type.includes('petcare') || type.includes('pet_care')) {
				output.petcare = true
			}
		})

		return output
	}, [membershipsAvailableForDate, firstDay])

	const handleSelection = useCallback(
		(value = {}) => {
			const newValues = { ...wizard_state, ...value }
			setWizardAttributeAndValues(newValues)
		},
		[wizard_state, setWizardAttributeAndValues]
	)

	const options = useMemo(() => {
		if (!daycare) {
			return []
		}

		const { early_dropoff, first_day_daycare } = wizard_state
		const { vip, petcare } = isVipOrPetCareOnDate
		const hasFreeEarlyDropoff = vip || petcare

		let formattedDate = 'the first day.'
		if (firstDay) {
			formattedDate = moment(firstDay).format('MM/DD')
		}

		const o = [
			{
				title: <div>Yes, with standard drop-off on {formattedDate}</div>,
				description: (
					<div>
						<div>I will drop-off between {daycare.standard_dropoff_hours}</div>
					</div>
				),
				disabled: loading || daycareNotAvailableOnDate,
				selected: first_day_daycare && !early_dropoff,
				onSelected: () => {
					const update = {
						[BOARDING_FIELDS.first_day_daycare]: true,
						[BOARDING_FIELDS.early_dropoff]: false
					}
					handleSelection(update)
				},
				style: { width: '22rem', maxWidth: 'unset' }
			},
			{
				title: <div>Yes, with early drop-off on {formattedDate}</div>,
				description: (
					<div>
						I will drop-off between {daycare.early_dropoff_hours}
						{hasFreeEarlyDropoff ? <div>Free with Membership</div> : null}
					</div>
				),
				disabled: loading || daycareNotAvailableOnDate,
				selected: first_day_daycare && early_dropoff,
				onSelected: () => {
					const update = {
						[BOARDING_FIELDS.first_day_daycare]: true,
						[BOARDING_FIELDS.early_dropoff]: true
					}
					handleSelection(update)
				},
				style: { width: '22rem', maxWidth: 'unset' }
			},
			{
				title: `No, I don't need daycare on ${formattedDate}`,
				description: `I will drop-off ${standardBoardingDropoffHours}`,
				disabled: loading || wizard_state[BOARDING_FIELDS.first_day_daycare] === false,
				selected: wizard_state[BOARDING_FIELDS.first_day_daycare] === false,
				onSelected: () => {
					const update = {
						[BOARDING_FIELDS.first_day_daycare]: false,
						[BOARDING_FIELDS.early_dropoff]: false
					}
					handleSelection(update)
				},
				style: { width: '22rem', maxWidth: 'unset' }
			}
		]

		return o
	}, [
		handleSelection,
		loading,
		wizard_state,
		daycare,
		firstDay,
		isVipOrPetCareOnDate,
		standardBoardingDropoffHours
	])

	return (
		<>
			<SectionFormContainer title={title} subtext={subtext}>
				<OptionsMenu options={options} />
				{daycareNotAvailableOnDate ? (
					<StyledAlert
						style={{ marginTop: '.8rem' }}
						type="warning"
						text="Daycare not available on this date"
					/>
				) : null}
			</SectionFormContainer>
		</>
	)
}

export default Dropoff
